import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getBlob, ref, uploadBytesResumable } from "firebase/storage";
import {
  Archive,
  ChevronRight,
  Download,
  File,
  FileArchive,
  FileAudio,
  FileImage,
  FileText,
  FileVideo,
  Folder,
  FolderPlus,
  HardDrive,
  History,
  RotateCcw,
  Upload,
  X,
} from "lucide-react";
import { db, functions, storage } from "./firebase";
import type {
  GameProject,
  WorkspaceFile,
  WorkspaceFileVersion,
  WorkspaceFolder,
} from "./types";

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const DEFAULT_QUOTA_BYTES = 5 * 1024 * 1024 * 1024;
const ACCEPTED_FILES = "image/*,audio/*,video/*,application/pdf,.zip,.rar,.7z";

function call(name: string, data: unknown) {
  return httpsCallable(functions, name)(data);
}

function makeId() {
  return crypto.randomUUID().replaceAll("-", "");
}

function safeStorageName(name: string) {
  return name.replace(/[^\p{L}\p{N}._() -]+/gu, "_").slice(0, 180);
}

function formatBytes(bytes: number) {
  if (!bytes) return "0 o";
  const units = ["o", "Ko", "Mo", "Go"];
  const unit = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  return `${(bytes / 1024 ** unit).toLocaleString("fr-CA", { maximumFractionDigits: unit ? 1 : 0 })} ${units[unit]}`;
}

function fileIcon(type: string) {
  if (type.startsWith("image/")) return <FileImage />;
  if (type.startsWith("audio/")) return <FileAudio />;
  if (type.startsWith("video/")) return <FileVideo />;
  if (type === "application/pdf") return <FileText />;
  if (type.includes("zip") || type.includes("rar") || type.includes("7z"))
    return <FileArchive />;
  return <File />;
}

export function ProjectFiles({
  project,
  trash = false,
  onNotice,
}: {
  project: GameProject;
  trash?: boolean;
  onNotice: (message: string) => void;
}) {
  const [files, setFiles] = useState<WorkspaceFile[]>([]);
  const [folders, setFolders] = useState<WorkspaceFolder[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState("");
  const [folderName, setFolderName] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<WorkspaceFile>();
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const versionInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const stopFiles = onSnapshot(
      collection(db, "gameProjects", project.id, "workspaceFiles"),
      (snapshot) =>
        setFiles(
          snapshot.docs.map(
            (item) => ({ id: item.id, ...item.data() }) as WorkspaceFile,
          ),
        ),
    );
    const stopFolders = onSnapshot(
      collection(db, "gameProjects", project.id, "workspaceFolders"),
      (snapshot) =>
        setFolders(
          snapshot.docs.map(
            (item) => ({ id: item.id, ...item.data() }) as WorkspaceFolder,
          ),
        ),
    );
    return () => {
      stopFiles();
      stopFolders();
    };
  }, [project.id]);

  useEffect(() => {
    if (!selected) return;
    const fresh = files.find((file) => file.id === selected.id);
    if (fresh) setSelected(fresh);
  }, [files, selected?.id]);

  useEffect(() => {
    let active = true;
    let url = "";
    if (!selected) {
      setPreviewUrl("");
      return;
    }
    getBlob(ref(storage, selected.storagePath))
      .then((blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      })
      .catch(() => active && onNotice("Aperçu impossible à charger."));
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [selected?.storagePath]);

  const breadcrumb = useMemo(() => {
    const result: WorkspaceFolder[] = [];
    let cursor = folders.find((folder) => folder.id === currentFolderId);
    while (cursor && result.length < 20) {
      result.unshift(cursor);
      cursor = folders.find((folder) => folder.id === cursor?.parentId);
    }
    return result;
  }, [currentFolderId, folders]);

  const visibleFolders = folders.filter((folder) =>
    trash
      ? folder.archived &&
        (!search || folder.name.toLowerCase().includes(search.toLowerCase()))
      : !folder.archived &&
        folder.parentId === currentFolderId &&
        (!search || folder.name.toLowerCase().includes(search.toLowerCase())),
  );
  const visibleFiles = files.filter((file) =>
    trash
      ? file.archived &&
        (!search || file.name.toLowerCase().includes(search.toLowerCase()))
      : !file.archived &&
        file.folderId === currentFolderId &&
        (!search || file.name.toLowerCase().includes(search.toLowerCase())),
  );

  async function uploadOne(file: globalThis.File, existing?: WorkspaceFile) {
    if (!file.size || file.size > MAX_FILE_BYTES)
      throw new Error("Chaque fichier doit peser entre 1 octet et 50 Mo.");
    const fileId = existing?.id || makeId();
    const versionId = makeId();
    const storagePath = `studio/${project.id}/${fileId}/${versionId}/${safeStorageName(file.name)}`;
    const task = uploadBytesResumable(ref(storage, storagePath), file, {
      contentType: file.type || "application/octet-stream",
    });
    await new Promise<void>((resolve, reject) => {
      task.on(
        "state_changed",
        (snapshot) =>
          setProgress(
            Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
          ),
        reject,
        resolve,
      );
    });
    await call("registerWorkspaceFileUpload", {
      projectId: project.id,
      fileId,
      versionId,
      folderId: existing?.folderId ?? currentFolderId,
      name: file.name,
      storagePath,
    });
  }

  async function uploadFiles(
    list: FileList | globalThis.File[],
    existing?: WorkspaceFile,
  ) {
    const batch = Array.from(list);
    if (!batch.length) return;
    setUploading(true);
    setProgress(0);
    try {
      for (const file of batch) await uploadOne(file, existing);
      onNotice(
        existing
          ? "Nouvelle version ajoutée."
          : `${batch.length} fichier${batch.length > 1 ? "s" : ""} téléversé${batch.length > 1 ? "s" : ""}.`,
      );
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Téléversement impossible.",
      );
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInput.current) fileInput.current.value = "";
      if (versionInput.current) versionInput.current.value = "";
    }
  }

  async function createFolder() {
    if (!folderName.trim()) return;
    try {
      await call("saveWorkspaceFolder", {
        projectId: project.id,
        parentId: currentFolderId,
        name: folderName,
      });
      setFolderName("");
      onNotice("Dossier créé.");
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Création impossible.");
    }
  }

  async function archiveFile(file: WorkspaceFile, archived: boolean) {
    try {
      await call("updateWorkspaceFile", {
        projectId: project.id,
        fileId: file.id,
        archived,
      });
      setSelected(undefined);
      onNotice(
        archived ? "Fichier placé dans la corbeille." : "Fichier restauré.",
      );
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Modification impossible.",
      );
    }
  }

  async function moveFile(file: WorkspaceFile, folderId: string) {
    try {
      await call("updateWorkspaceFile", {
        projectId: project.id,
        fileId: file.id,
        folderId,
      });
      onNotice("Fichier déplacé.");
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Déplacement impossible.",
      );
    }
  }

  async function archiveFolder(folder: WorkspaceFolder, archived: boolean) {
    try {
      await call("setWorkspaceFolderArchived", {
        projectId: project.id,
        folderId: folder.id,
        archived,
      });
      onNotice(
        archived ? "Dossier placé dans la corbeille." : "Dossier restauré.",
      );
    } catch (error) {
      onNotice(
        error instanceof Error ? error.message : "Modification impossible.",
      );
    }
  }

  async function downloadVersion(version: WorkspaceFileVersion) {
    try {
      const blob = await getBlob(ref(storage, version.storagePath));
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = version.name;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      onNotice("Téléchargement impossible.");
    }
  }

  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (!trash && !uploading) void uploadFiles(event.dataTransfer.files);
  }

  const used = project.storageBytes || 0;
  const quota = project.storageQuotaBytes || DEFAULT_QUOTA_BYTES;
  return (
    <section className="file-manager">
      <header className="file-manager-header">
        <div>
          <small>{trash ? "RÉCUPÉRATION" : "STOCKAGE DU PROJET"}</small>
          <h3>{trash ? "Corbeille" : "Fichiers du projet"}</h3>
          <p>
            {trash
              ? "Restaurez les fichiers et dossiers retirés."
              : "Images, sons, vidéos, PDF et archives, accessibles à l’équipe."}
          </p>
        </div>
        {!trash && (
          <label className={`button ${uploading ? "disabled" : ""}`}>
            <Upload /> {uploading ? `${progress} %` : "Téléverser"}
            <input
              ref={fileInput}
              hidden
              type="file"
              multiple
              accept={ACCEPTED_FILES}
              disabled={uploading}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                event.target.files && void uploadFiles(event.target.files)
              }
            />
          </label>
        )}
      </header>
      {!trash && (
        <div className="storage-meter">
          <HardDrive />
          <div>
            <span
              style={{ width: `${Math.min(100, (used / quota) * 100)}%` }}
            />
          </div>
          <small>
            {formatBytes(used)} sur {formatBytes(quota)} utilisés · 50 Mo
            maximum par fichier
          </small>
        </div>
      )}
      <div className="file-toolbar">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Rechercher un fichier ou dossier…"
        />
        {!trash && (
          <div className="folder-create">
            <input
              value={folderName}
              maxLength={100}
              onChange={(event) => setFolderName(event.target.value)}
              onKeyDown={(event) =>
                event.key === "Enter" && void createFolder()
              }
              placeholder="Nouveau dossier"
            />
            <button
              className="ghost"
              onClick={() => void createFolder()}
              disabled={!folderName.trim()}
            >
              <FolderPlus /> Créer
            </button>
          </div>
        )}
      </div>
      {!trash && (
        <nav className="file-breadcrumb">
          <button onClick={() => setCurrentFolderId("")}>Racine</button>
          {breadcrumb.map((folder) => (
            <span key={folder.id}>
              <ChevronRight />
              <button onClick={() => setCurrentFolderId(folder.id)}>
                {folder.name}
              </button>
            </span>
          ))}
        </nav>
      )}
      <div
        className={`file-dropzone ${uploading ? "uploading" : ""}`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={drop}
      >
        {visibleFolders.map((folder) => (
          <article className="file-card folder" key={folder.id}>
            <button
              className="file-card-main"
              onClick={() => !trash && setCurrentFolderId(folder.id)}
            >
              <Folder />
              <span>
                <strong>{folder.name}</strong>
                <small>Dossier</small>
              </span>
            </button>
            <button
              className="icon-button"
              title={trash ? "Restaurer" : "Mettre à la corbeille"}
              onClick={() => void archiveFolder(folder, !trash)}
            >
              {trash ? <RotateCcw /> : <Archive />}
            </button>
          </article>
        ))}
        {visibleFiles.map((file) => (
          <article className="file-card" key={file.id}>
            <button
              className="file-card-main"
              onClick={() => setSelected(file)}
            >
              {fileIcon(file.contentType)}
              <span>
                <strong>{file.name}</strong>
                <small>
                  {formatBytes(file.size)} · {file.versionCount} version
                  {file.versionCount > 1 ? "s" : ""}
                </small>
              </span>
            </button>
            <button
              className="icon-button"
              title={trash ? "Restaurer" : "Mettre à la corbeille"}
              onClick={() => void archiveFile(file, !trash)}
            >
              {trash ? <RotateCcw /> : <Archive />}
            </button>
          </article>
        ))}
        {!visibleFiles.length && !visibleFolders.length && (
          <div className="file-empty">
            <Folder />
            <strong>
              {trash ? "La corbeille est vide" : "Ce dossier est vide"}
            </strong>
            <span>
              {trash
                ? "Les éléments retirés apparaîtront ici."
                : "Glissez des fichiers ici ou utilisez Téléverser."}
            </span>
          </div>
        )}
      </div>
      {selected && (
        <div
          className="ticket-modal"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setSelected(undefined)
          }
        >
          <article className="project-form ticket-editor file-preview-modal">
            <header>
              <div>
                <span className="eyebrow">
                  Fichier · {formatBytes(selected.size)}
                </span>
                <h2>{selected.name}</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setSelected(undefined)}
              >
                <X />
              </button>
            </header>
            <div className="file-preview">
              {!previewUrl && <p>Chargement de l’aperçu…</p>}
              {previewUrl && selected.contentType.startsWith("image/") && (
                <img src={previewUrl} alt={selected.name} />
              )}
              {previewUrl && selected.contentType.startsWith("audio/") && (
                <audio src={previewUrl} controls />
              )}
              {previewUrl && selected.contentType.startsWith("video/") && (
                <video src={previewUrl} controls />
              )}
              {previewUrl && selected.contentType === "application/pdf" && (
                <iframe src={previewUrl} title={selected.name} />
              )}
              {previewUrl &&
                !/^(image|audio|video)\//.test(selected.contentType) &&
                selected.contentType !== "application/pdf" && <FileArchive />}
            </div>
            <div className="file-actions">
              <button
                className="button"
                onClick={() =>
                  void downloadVersion(
                    selected.versions[selected.versions.length - 1],
                  )
                }
              >
                <Download /> Télécharger
              </button>
              {!selected.archived && (
                <label className="ghost">
                  <Upload /> Nouvelle version
                  <input
                    ref={versionInput}
                    hidden
                    type="file"
                    accept={ACCEPTED_FILES}
                    disabled={uploading}
                    onChange={(event) =>
                      event.target.files &&
                      void uploadFiles(event.target.files, selected)
                    }
                  />
                </label>
              )}
              {!selected.archived && (
                <label className="file-move">
                  Déplacer vers
                  <select
                    value={selected.folderId}
                    onChange={(event) =>
                      void moveFile(selected, event.target.value)
                    }
                  >
                    <option value="">Racine</option>
                    {folders
                      .filter((folder) => !folder.archived)
                      .map((folder) => (
                        <option key={folder.id} value={folder.id}>
                          {folder.name}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              <button
                className="ghost"
                onClick={() => void archiveFile(selected, !selected.archived)}
              >
                {selected.archived ? <RotateCcw /> : <Archive />}
                {selected.archived ? "Restaurer" : "Corbeille"}
              </button>
            </div>
            <section className="file-versions">
              <h3>
                <History /> Historique des versions
              </h3>
              {[...selected.versions].reverse().map((version, index) => (
                <article key={version.id}>
                  <span>
                    <strong>Version {selected.versions.length - index}</strong>
                    <small>
                      {version.authorName} · {formatBytes(version.size)} ·{" "}
                      {version.createdAt
                        ?.toDate?.()
                        .toLocaleDateString("fr-CA") || "Aujourd’hui"}
                    </small>
                  </span>
                  <button
                    className="icon-button"
                    title="Télécharger cette version"
                    onClick={() => void downloadVersion(version)}
                  >
                    <Download />
                  </button>
                </article>
              ))}
            </section>
          </article>
        </div>
      )}
    </section>
  );
}
